import { LoadingButton } from '@mui/lab';
import { Box, Typography } from '@mui/material';
import { useForm } from 'react-hook-form';
import Modal from 'src/components/customModal';
import { useTask } from 'src/components/GeneralTab/components/TasksSection/components/Task/hooks/useTask';
import { styledMainTitle } from 'src/components/InviteModal/styles';
import modals from 'src/constants/modals';
import useLoading from 'src/hooks/useLoading';
import { selectModal } from 'src/redux/slices/modal';
import { dispatch, useSelector } from 'src/redux/store';
import { Task } from 'src/redux/types';
import { handleCloseModal } from 'src/utils/propertyHelpers';
import { yupResolver } from '@hookform/resolvers/yup';
import { FormProvider, RHFAutocomplete } from 'src/components/hook-form';
import { getTaskRef } from 'src/utils/firebase';
import { updateDoc } from 'firebase/firestore';
import { getTransactionTrackerThunk, selectTransactionOverview } from 'src/redux/slices/transaction';
import { labelByTrackerStage } from 'src/components/TransactionSummary/constants';
import { MoveTaskForm } from './types';
import { moveTaskValidationSchema } from './validation';
import { trackerStagesByStep, trackerStepsOptions } from './constants';

const Content = () => {
	const { isLoading, setIsLoading } = useLoading();
	const transaction = useSelector(selectTransactionOverview);
	const { payload } = useSelector(selectModal<Task>);
	const { description, statement } = useTask(payload);
	const form = useForm<MoveTaskForm>({
		resolver: yupResolver(moveTaskValidationSchema),
		defaultValues: { step: payload.step, stage: payload.stage },
	});

	const step = form.watch('step');

	const taskRef = getTaskRef(transaction.id, payload.id);

	const handleSubmit = async (data: MoveTaskForm) => {
		setIsLoading(true);

		await updateDoc(taskRef, { step: data.step, stage: data.stage });
		dispatch(getTransactionTrackerThunk({ id: transaction.id, withLoading: true }));

		setIsLoading(false);
		handleCloseModal();
	};

	const trackerStepStages = trackerStagesByStep[step] ?? [];

	const trackerStagesOptions = trackerStepStages.map((stage) => ({ key: stage, name: labelByTrackerStage[stage] }));

	return (
		<Box>
			<FormProvider
				methods={form}
				onSubmit={(e) => {
					e.stopPropagation();
					form.handleSubmit(handleSubmit)(e);
				}}
			>
				<Typography sx={styledMainTitle}>MOVE TASK</Typography>
				<Typography variant='subtitle1' display='inline'>
					Which stage should "{description ?? statement}" move to?
				</Typography>
				<RHFAutocomplete
					name='step'
					label='Select tracker step'
					options={trackerStepsOptions}
					valueAttribute='key'
					sx={{ mt: '15px' }}
				/>
				<RHFAutocomplete
					name='stage'
					label='Select tracker stage'
					options={trackerStagesOptions}
					valueAttribute='key'
					sx={{ mt: '15px' }}
				/>
				<Box display='flex' justifyContent='flex-end' gap='15px' alignItems='center' mt='20px'>
					<LoadingButton variant='outlined' onClick={handleCloseModal} loading={isLoading}>
						Cancel
					</LoadingButton>
					<LoadingButton variant='contained' type='submit' loading={isLoading}>
						Move
					</LoadingButton>
				</Box>
			</FormProvider>
		</Box>
	);
};

const MoveTaskModal = () => (
	<Modal name={modals.moveTask} cardSx={{ maxWidth: '600px', overflow: 'visible' }}>
		<Content />
	</Modal>
);

export default MoveTaskModal;
